import manifest from "@/data/results-images.json";
import cdn from "@/data/results-cdn.json";

export type ImgRef = {
  url: string;
  srcSet?: string;
  sizes?: string;
  width?: number;
  height?: number;
};

type Entry = { w: number; h: number; v: number[] };

const images = manifest as Record<string, Entry>;
const cdnUrls = cdn as Record<string, string>;

/**
 * Stable, immutably cached CDN URL for a gallery photo file.
 *
 * Files under /public/img are served by the static layer without any
 * Cache-Control header, so every visit re-downloads them. The same bytes
 * uploaded through the asset pipeline are served with
 * `max-age=31536000, immutable`, which is what the homepage hero uses.
 * Falls back to the public path if a file has not been uploaded yet.
 */
export function galleryUrl(file: string): string {
  return cdnUrls[file] ?? `/img/results/${file}`;
}

/**
 * Build a responsive WebP image reference for a before/after results photo.
 * Variants are pre-generated at build time and served from the asset CDN.
 */
export function resultImg(
  stem: string,
  sizes = "(min-width: 768px) 17rem, 50vw",
): ImgRef {
  const entry = images[stem];
  if (!entry) {
    return { url: galleryUrl(`${stem}-400.webp`), sizes };
  }
  const srcSet = entry.v
    .map((w) => `${galleryUrl(`${stem}-${w}.webp`)} ${w}w`)
    .join(", ");
  const smallest = entry.v[0];
  return {
    url: galleryUrl(`${stem}-${smallest}.webp`),
    srcSet,
    sizes,
    width: entry.w,
    height: entry.h,
  };
}
